/**
 * Core colour value types.
 *
 * Every type here is a plain data object: no classes, no DOM, no I/O. A colour
 * value never carries its own illuminant — the illuminant is a parameter of the
 * *conversion*, and callers are responsible for keeping track of which space a
 * value is in. This keeps the types trivially serialisable.
 */

/** Non-linear (gamma-encoded) sRGB. Components nominally in [0, 1]. */
export interface SRGB {
  r: number;
  g: number;
  b: number;
}

/** Linear-light RGB using sRGB primaries. Components nominally in [0, 1]. */
export interface LinearRGB {
  r: number;
  g: number;
  b: number;
}

/** CIE 1931 XYZ, scaled so that the reference white has Y = 1. */
export interface XYZ {
  x: number;
  y: number;
  z: number;
}

/** CIE L*a*b*. L in [0, 100]; a and b unbounded but typically in [-128, 127]. */
export interface Lab {
  L: number;
  a: number;
  b: number;
}

/** Cylindrical form of Lab. h is in degrees, [0, 360). */
export interface LCh {
  L: number;
  C: number;
  h: number;
}

/** Standard illuminants this library can convert between. */
export type Illuminant = 'D65' | 'D50';

/**
 * CIE 1931 2-degree observer white points, normalised to Y = 1.
 *
 * These are the ASTM E308 tabulated values, which are also what ICC profiles
 * use. Note that deriving D65 from its chromaticity coordinates (0.3127,
 * 0.3290) instead gives (0.950456, 1, 1.089058) — very slightly different, and
 * enough to shift sRGB red from Lab L*=53.2408 to 53.2371.
 *
 * The tabulated values are the right choice here: they are the convention in
 * paint and coatings measurement, and they are what published reference data
 * is quoted against. The sRGB matrices in `xyz.ts` are derived from the D65
 * value below so that linear white maps onto it exactly.
 */
export const WHITE_POINTS: Record<Illuminant, XYZ> = {
  D65: { x: 0.95047, y: 1, z: 1.08883 },
  D50: { x: 0.96422, y: 1, z: 0.82521 },
};

/** Row-major 3x3 matrix. */
export type Matrix3 = readonly [
  number, number, number,
  number, number, number,
  number, number, number,
];

/** Apply a row-major 3x3 matrix to a 3-vector. */
export function applyMatrix3(m: Matrix3, v: readonly [number, number, number]): [number, number, number] {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

export const IDENTITY_3: Matrix3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];
